import { Button, Divider, Modal, Spin, Typography } from 'antd';
import { stringify } from 'csv-stringify/browser/esm/sync';
import { format } from 'date-fns';
import FileSaver from 'file-saver';
import { useState } from 'react';
import type { DerivedTeamRoster } from '../../../hooks/types';
import { useLoggedInUser } from '../../../hooks/useLoggedInUser';
import { useNotify } from '../../../hooks/useNotify';
import { usePermission } from '../../../hooks/usePermission';
import { useLazyQueryTeamRoster } from '../../../hooks/useQueryTeamRoster';
import { onApolloError } from '../../../lib/graphql/helpers';
import { rostersToCsvData } from '../../../lib/utils/csv-report';
import rfsTheme from '../../../theme';
import DatePicker, { onRangeChange } from '../../DatePicker';

const { Title } = Typography;
const { RangePicker } = DatePicker;

type ExportShiftManagerFuncProps = {
  readonly toggleExportModal: () => void;
};

type ExportShiftManagerProps = ExportShiftManagerFuncProps & {
  readonly teamId: string;
  readonly teamName: string;
  readonly defaultFromDate: Date;
  readonly defaultToDate: Date;
};

/**
 * Construct csv data for target roster and download the csv file.
 */
const onRosterQueryComplete = (
  currentTeamRoster: DerivedTeamRoster | undefined,
  teamName: string,
  fromDate: number | Date,
  toDate: number | Date,
  closeModalFn: () => void,
  isPowerUser: boolean
) => {
  const csvText = stringify(
    rostersToCsvData(
      currentTeamRoster === undefined ? [] : [currentTeamRoster],
      isPowerUser,
      'all shifts'
    ).slice()
  );

  const fileName = `${teamName}-roster_${format(
    fromDate,
    'yyyy-MM-dd'
  )}_to_${format(toDate, 'yyyy-MM-dd')}.csv`;

  const data = new Blob([csvText], { type: 'text/csv' });

  FileSaver.saveAs(data, fileName);

  closeModalFn();
};

/**
 * Renders a section to export shifts in specific range for a team's roster.
 */
const ExportShiftManager = ({
  teamId,
  teamName,
  defaultFromDate,
  defaultToDate,
  toggleExportModal,
}: ExportShiftManagerProps) => {
  const { notify } = useNotify();
  const { loggedInUser } = useLoggedInUser();
  const permission = usePermission(undefined, loggedInUser);

  const [fromDate, setFromDate] = useState<Date>(defaultFromDate);
  const [toDate, setToDate] = useState<Date>(defaultToDate);

  const [getRosters, { loading: loadingTeamRosterData }] =
    useLazyQueryTeamRoster(
      fromDate,
      toDate,
      onApolloError(notify.error),
      teamId,
      undefined,
      (teamRosters) =>
        onRosterQueryComplete(
          teamRosters[0],
          teamName,
          fromDate,
          toDate,
          toggleExportModal,
          permission.isPowerUser
        )
    );

  const triggerExport = () => void getRosters();

  return (
    <>
      <Title level={5}>{`Export shifts for team ${teamName}`}</Title>
      <label htmlFor="export-range-picker" style={{ verticalAlign: 'middle' }}>
        In Range &nbsp;
      </label>
      <RangePicker
        id="export-range-picker"
        allowClear={false}
        direction="ltr"
        value={[fromDate, toDate]}
        onChange={onRangeChange(setFromDate, setToDate)}
      />
      <Divider />
      <div style={{ marginTop: '20px', textAlign: 'right', minHeight: '40px' }}>
        <Button
          className="cancel-export-btn"
          onClick={toggleExportModal}
          style={{
            float: 'left',
            color: rfsTheme.darkRfsRed,
            borderColor: rfsTheme.darkRfsRed,
          }}
        >
          Cancel
        </Button>
        <Button
          className="save-shift-btn"
          type="primary"
          onClick={triggerExport}
          disabled={loadingTeamRosterData}
        >
          {loadingTeamRosterData ? <Spin tip={'Exporting'} /> : 'Export'}
        </Button>
      </div>
    </>
  );
};

const ExportShiftManagerButton: React.FC<ExportShiftManagerProps> = (props) => {
  const [showExportModal, setShowExportModal] = useState(false);

  const toggleExportModal = () => setShowExportModal(!showExportModal);

  return (
    <>
      <Button
        className="export-shift-btn"
        onClick={toggleExportModal}
        style={{ width: '100%' }}
      >
        CSV
      </Button>

      <Modal
        className="export-team-roster-modal"
        title="Export Roster Shifts"
        open={showExportModal}
        footer={null}
        destroyOnClose
        onCancel={() => setShowExportModal(false)}
      >
        <ExportShiftManager {...props} />
      </Modal>
    </>
  );
};

export default ExportShiftManagerButton;
